import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CardHeader,
  IconButton,
  Stack,
  Button,
  Popover,
  Box,
  TextField,
  Checkbox,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete as DeleteIcon,
  Print,
  Visibility,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import config from "../../../config";
import { generatePdf } from "../Sale/SalesOrderPrint";
import {
  getDashboardCounts,
  SalesOrderListApi,
  ApproveSo,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

// === Card Icons ===
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const cardColors = [
  "linear-gradient(135deg, #6a11cb 0%, #8e54e9 100%)",
  "linear-gradient(135deg, #ff8008 0%, #ffb347 100%)",
  "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)",
  "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
  "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)",
  "linear-gradient(135deg, #ff512f 0%, #dd2476 100%)",
  "linear-gradient(135deg, #373B44 0%, #4286f4 100%)",
];

export default function Dashboard() {
  const [counts, setCounts] = useState({});
  const [salesOrders, setSalesOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [soToApprove, setSoToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForOrderId, setShowRemarkForOrderId] = useState(null);

  const [filters, setFilters] = useState({
    company_name: "",
    client_name: "",
    id: "",
    project_name: "",
    status: "Pending",
  });

  const navigate = useNavigate();
  const baseURL = config.staticBaseURL;

  // 🔹 Responsive helpers
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedSoId, setExpandedSoId] = useState(null); // mobile card expand state

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchOrders(1, true, filters);
    }, 1000);
    return () => clearTimeout(handler);
  }, [filters]);

  const fetchCounts = async () => {
    try {
      const res = await getDashboardCounts();
      setCounts(res);
    } catch (err) {
      console.error("Failed to fetch dashboard counts", err);
    }
  };

  const fetchOrders = async (
    pageNum = 1,
    reset = false,
    filterValues = filters
  ) => {
    const data = await SalesOrderListApi(pageNum, filterValues);

    if (reset || pageNum === 1) {
      setSalesOrders(data.results || []);
    } else {
      setSalesOrders((prev) => [...prev, ...data.results]);
    }

    setHasMore(data.next !== null);
    setPage(pageNum);
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage);
  };

  const handleApprove = async (order, status, remarks = "") => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveSo(
        order.id,
        approvedById,
        order.status,
        remarkInput
      );

      setSalesOrders((prev) =>
        prev.map((item) => (item.id === order.id ? updated : item))
      );

      toast.success(
        updated.status === "Approved"
          ? "SO approved successfully!"
          : "SO unapproved successfully!"
      );
    } catch (error) {
      console.error("Approval Toggle Error:", error);
      toast.error("Failed to toggle SO approval.");
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const cardData = [
    {
      title: "Total SO",
      path: "/dashboard/saleorderlist",
      icon: <PeopleIcon fontSize="large" />,
      value: counts.projects?.total || 0,
    },
    {
      title: "Approved SO",
      path: "/dashboard/saleorderlist",
      icon: <BarChartIcon fontSize="large" />,
      value: counts.projects?.approved || 0,
    },
    {
      title: "Pending SO",
      path: "/dashboard/saleorderlist",
      icon: <TrendingUpIcon fontSize="large" />,
      value: counts.projects?.pending || 0,
    },
    {
      title: "Today SO",
      path: "/dashboard/saleorderlist",
      icon: <ShoppingCartIcon fontSize="large" />,
      value: counts.projects?.today || 0,
    },
    {
      title: "Total PI",
      path: "/dashboard/performatable",
      icon: <ReceiptLongIcon fontSize="large" />,
      value: counts.proforma_invoices?.total || 0,
    },
    {
      title: "Approved PI",
      path: "/dashboard/performatable",
      icon: <InventoryIcon fontSize="large" />,
      value: counts.proforma_invoices?.approved || 0,
    },
    {
      title: "Pending PI",
      path: "/dashboard/performatable",
      icon: <CheckCircleIcon fontSize="large" />,
      value: counts.proforma_invoices?.pending || 0,
    },
    {
      title: "Today PI",
      path: "/dashboard/performatable",
      icon: <ThumbUpIcon fontSize="large" />,
      value: counts.proforma_invoices?.today || 0,
    },
  ];

  function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  // 🔹 MOBILE CARD for Sale Orders
  const MobileSoCard = ({ order, index }) => {
    const isExpanded = expandedSoId === order.id;
    const defaultBg = index % 2 === 0 ? "#fafafa" : "#ffffff";

    const statusLabel =
      order.status === "Approved"
        ? "Approved"
        : order.status === "Rejected"
          ? "Rejected"
          : "Pending";

    const statusColor =
      order.status === "Approved"
        ? "green"
        : order.status === "Rejected"
          ? "red"
          : "#ff9800";

    const statusBg =
      order.status === "Approved"
        ? "rgba(56, 142, 60, 0.12)"
        : order.status === "Rejected"
          ? "rgba(211, 47, 47, 0.12)"
          : "rgba(255, 152, 0, 0.12)";

    return (
      <Box key={order.id}>
        {/* Header Row */}
        <Box
          onClick={() =>
            setExpandedSoId(isExpanded ? null : order.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.3,
            cursor: "pointer",
            bgcolor: defaultBg,
            borderLeft: "4px solid #1976d2",
          }}
        >
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#666",
                mb: 0.3,
              }}
            >
              #{index + 1} • {formatOnlyDate(order.created_at)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.85rem",
                mb: 0.1,
              }}
            >
              {order.project_name || "-"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#555",
              }}
            >
              {order.party_details?.company_name || "-"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#777",
              }}
            >
              SO No: {order.so_no}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            {userRole != "Staff" && (
              <Checkbox
                checked={
                  order.status === "Approved" ||
                  order.status === "Rejected"
                }
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setApproveAnchorEl(e.currentTarget);
                  setSoToApprove(order);
                }}
                sx={{
                  padding: 0,
                  transform: "scale(0.8)",
                  color:
                    order.status === "Approved"
                      ? "green"
                      : order.status === "Rejected"
                        ? "red"
                        : "grey",
                  "&.Mui-checked": {
                    color:
                      order.status === "Approved" ? "green" : "red",
                  },
                }}
              />
            )}

            <Typography
              sx={{
                fontSize: "0.7rem",
                px: 0.8,
                py: 0.2,
                borderRadius: "999px",
                bgcolor: statusBg,
                color: statusColor,
                fontWeight: 500,
              }}
            >
              {statusLabel}
            </Typography>

            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSoId(isExpanded ? null : order.id);
              }}
            >
              {isExpanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  CONTACT PERSON
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {order.party_details?.name || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  CONTACT NO.
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {order.party_details?.contact || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  EMAIL
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                    wordBreak: "break-all",
                  }}
                >
                  {order.party_details?.email || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  APPROVED BY
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {order.approved_name || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  GST
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {order.party_details?.gst || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  PAN
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#555",
                  }}
                >
                  {order.party_details?.pan || "-"}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  ADDRESS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    lineHeight: 1.3,
                  }}
                >
                  {order.party_details?.address || "-"}
                </Typography>
              </Box>

              {order.remarks && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    REMARKS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#555",
                      lineHeight: 1.3,
                    }}
                  >
                    {order.remarks}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.6,
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  generatePdf(order);
                }}
                sx={{
                  bgcolor: "#e3f2fd",
                  borderRadius: "6px",
                  width: 30,
                  height: 30,
                }}
              >
                <Print sx={{ fontSize: "1rem", color: "#1976d2" }} />
              </IconButton>

              {order.project && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`${order.project}`, "_blank");
                  }}
                  sx={{
                    bgcolor: "#f3e5f5",
                    borderRadius: "6px",
                    width: 30,
                    height: 30,
                  }}
                >
                  <Visibility
                    sx={{ fontSize: "1rem", color: "#7b1fa2" }}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
          <Divider />
        </Collapse>
      </Box>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      {/* ==== CARDS ==== */}
      <Grid container spacing={2} sx={{ mt: -3 }}>
        {cardData.map((card, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={index}
            sx={{ mt: -4, mb: 4 }}
          >
            <Card
              onClick={() => navigate(card.path)}
              sx={{
                height: 100,
                borderRadius: 0,
                background: cardColors[index],
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                p: 2,
                transition:
                  "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
              <div style={{ marginRight: 12 }}>{card.icon}</div>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {card.title}
                </Typography>
                <Typography variant="caption" display="block">
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ==== Pending Sale Orders ==== */}
      <Card sx={{ boxShadow: 3,}}>
        <CardHeader
          stickyHeader
          title="Pending Sale Orders"
          titleTypographyProps={{
            variant: "h6",
            fontWeight: "bold",
            color: "black",
          }}
          sx={{ pb: 0 }}
        />

        <CardContent>
          <InfiniteScroll
            dataLength={salesOrders.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Typography textAlign="center" p={2}>
                Loading...
              </Typography>
            }
          // height={isMobile ? "65vh" : "70vh"}
          >
            {isMobile ? (
              <Box sx={{ bgcolor: "background.paper", pb: 1 }}>
                {salesOrders.length > 0 ? (
                  salesOrders.map((order, index) => (
                    <MobileSoCard
                      key={order.id || index}
                      order={order}
                      index={index}
                    />
                  ))
                ) : (
                  <Typography textAlign="center" p={3}>
                    No records found
                  </Typography>
                )}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 0,
                  maxHeight: 290,
                  overflowY: "auto",
                }}
              >
                <Table
                  hover
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: "max-content",
                    "& td, & th": {
                      borderBottom: "1px solid #ccc",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? "#2196f3"
                            : "#333",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : "#f5f5f5",
                      },
                    }}
                  >
                    <TableRow
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": {
                          fontWeight: "bold",
                          color: "#333",
                          borderBottom: "1px solid #999",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell>Approve</TableCell>
                      )}

                      <TableCell align="center">S.No.</TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          minWidth: 100,
                          backgroundColor: "#fff",
                          zIndex: 3,
                        }}
                        align="center"
                      >
                        So. No.
                      </TableCell>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Project</TableCell>
                      <TableCell align="center">
                        Contact Person
                      </TableCell>
                      <TableCell align="center">Company</TableCell>
                      <TableCell align="center">
                        Contact No.
                      </TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">
                        Approved By
                      </TableCell>
                      <TableCell align="center">Email</TableCell>
                      <TableCell align="center">GST</TableCell>
                      <TableCell align="center">PAN</TableCell>
                      <TableCell align="center">Address</TableCell>
                      <TableCell align="center">Remarks</TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          minWidth: 140,
                          backgroundColor: "#fff",
                          zIndex: 3,
                        }}
                        align="center"
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <ToastContainer />
                  <TableBody>
                    {salesOrders.map((order, idx) => {
                      const defaultBg =
                        idx % 2 === 0 ? "#fafafa" : "#ffffff";
                      const hoverBg = "#e3f2fd ";
                      return (
                        <TableRow
                          hover
                          key={idx}
                          sx={{
                            backgroundColor: defaultBg,
                            "&:hover": {
                              backgroundColor: hoverBg,
                              "& td": {
                                backgroundColor: hoverBg,
                              },
                            },
                            "& td": {
                              paddingTop: "2px !important",
                              paddingBottom: "2px !important",
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.8125rem",
                              },
                              whiteSpace: "nowrap",
                            },
                          }}
                        >
                          {userRole != "Staff" && (
                            <TableCell align="center">
                              <Checkbox
                                checked={
                                  order.status === "Approved" ||
                                  order.status === "Rejected"
                                }
                                onChange={(e) => {
                                  setApproveAnchorEl(e.currentTarget);
                                  setSoToApprove(order);
                                }}
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                  transform: "scale(0.8)",
                                  color:
                                    order.status === "Approved"
                                      ? "green"
                                      : order.status === "Rejected"
                                        ? "red"
                                        : "grey",
                                  "&.Mui-checked": {
                                    color:
                                      order.status === "Approved"
                                        ? "green"
                                        : "red",
                                  },
                                }}
                              />
                            </TableCell>
                          )}

                          <TableCell align="center">
                            {idx + 1}
                          </TableCell>

                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              minWidth: 100,
                              backgroundColor: defaultBg,
                              zIndex: 2,
                            }}
                            align="center"
                          >
                            {order.so_no}
                          </TableCell>

                          <TableCell>
                            {formatOnlyDate(order.created_at)}
                          </TableCell>
                          <TableCell align="center">
                            {order.project_name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.name}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.company_name}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.contact}
                          </TableCell>
                          <TableCell align="center">
                            {order.status === "Approved"
                              ? "Approved"
                              : order.status === "Rejected"
                                ? "Rejected"
                                : "Pending"}
                          </TableCell>
                          <TableCell align="center">
                            {order.approved_name}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.email}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.gst || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.pan || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {order.party_details?.address || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {order.remarks}
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              minWidth: 140,
                              backgroundColor: defaultBg,
                              zIndex: 2,
                            }}
                            align="center"
                          >
                            <IconButton
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                              size="small"
                              color="default"
                              onClick={() => generatePdf(order)}
                            >
                              <Print fontSize="small" />
                            </IconButton>
                            {order.project ? (
                              <IconButton
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                }}
                                size="small"
                                color="info"
                                onClick={() =>
                                  window.open(
                                    `${order.project}`,
                                    "_blank"
                                  )
                                }
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </InfiniteScroll>
        </CardContent>
      </Card>

      {/* Approve/Reject Popover same as before */}
      <Popover
        open={Boolean(approveAnchorEl)}
        anchorEl={approveAnchorEl}
        onClose={() => {
          setApproveAnchorEl(null);
          setSoToApprove(null);
          setRemarkInput("");
          setShowRemarkForOrderId(null);
          setActionType(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          {showRemarkForOrderId === soToApprove?.id ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Please enter remark before{" "}
                {actionType === "Approved"
                  ? "approving"
                  : "rejecting"}
                :
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter remark"
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                <Button
                  color={
                    actionType === "Approved"
                      ? "success"
                      : "error"
                  }
                  size="small"
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required!");
                      return;
                    }
                    handleApprove({
                      ...soToApprove,
                      status: actionType,
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForOrderId(null);
                    setActionType(null);
                  }}
                >
                  Yes
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForOrderId(null);
                    setActionType(null);
                  }}
                >
                  No
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Do you want to approve or reject this Sales order?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                <Button
                  color="success"
                  size="small"
                  onClick={() => {
                    setShowRemarkForOrderId(soToApprove?.id);
                    setActionType("Approved");
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    setShowRemarkForOrderId(soToApprove?.id);
                    setActionType("Rejected");
                  }}
                >
                  Reject
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </div>
  );
}